import React, { useState, useRef, useEffect } from 'react';

/**
 * FirmField — a labeled, click-to-edit field matching the candidate widgets'
 * CompField design: small gray label, value shown in a filled gray box that
 * reveals a pencil on hover; click to edit, debounced autosave, Enter/blur
 * commits, Esc cancels.
 *
 * Props:
 *   label     — field label
 *   value     — current value
 *   onSave(v) — persist handler (called after debounce / on commit)
 *   type      — 'text' | 'number' | 'date' | 'select'
 *   options   — [{value,label}] for select
 *   display   — optional formatter for the read view (string or node)
 *   placeholder
 */
export default function FirmField({ label, value, onSave, type = 'text', options = [], display, placeholder = 'Click to add', accent, multiline = false }) {
    const [editing, setEditing] = useState(false);
    const [temp, setTemp] = useState(value ?? '');
    const debounceRef = useRef(null);
    const inputRef = useRef(null);

    const labelClass = accent === 'emerald' ? 'block text-xs font-medium text-emerald-800 mb-1' : 'block text-xs text-gray-600 mb-1';
    const boxClass = accent === 'emerald'
        ? 'flex items-center justify-between cursor-pointer group'
        : 'flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors';
    const valueClass = accent === 'emerald' ? 'text-lg font-bold text-emerald-700 truncate' : 'text-sm text-gray-800 truncate';

    useEffect(() => { setTemp(value ?? ''); }, [value]);
    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            if (inputRef.current.select && type !== 'date') inputRef.current.select();
        }
    }, [editing, type]);

    const scheduleSave = (v) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => onSave(v === '' ? null : v), 500);
    };
    const commit = (v) => {
        if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
        if (String(v ?? '') !== String(value ?? '')) onSave(v === '' ? null : v);
        setEditing(false);
    };
    const cancel = () => {
        if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
        setTemp(value ?? '');
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="mb-2">
                {label && <label className={labelClass}>{label}</label>}
                {type === 'select' ? (
                    <select
                        ref={inputRef}
                        value={temp ?? ''}
                        onChange={(e) => { setTemp(e.target.value); commit(e.target.value); }}
                        onBlur={() => setEditing(false)}
                        className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                ) : multiline ? (
                    <textarea
                        ref={inputRef}
                        value={temp ?? ''}
                        rows={4}
                        onChange={(e) => setTemp(e.target.value)}
                        onBlur={() => commit(temp)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit(temp);
                            else if (e.key === 'Escape') cancel();
                        }}
                        placeholder={placeholder}
                        className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                ) : (
                    <input
                        ref={inputRef}
                        type={type}
                        value={temp ?? ''}
                        onChange={(e) => { setTemp(e.target.value); scheduleSave(e.target.value); }}
                        onBlur={() => commit(temp)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commit(temp);
                            else if (e.key === 'Escape') cancel();
                        }}
                        placeholder={placeholder}
                        className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                )}
            </div>
        );
    }

    return (
        <div className={accent === 'emerald' ? 'group' : 'mb-2 group'}>
            {label && <label className={labelClass}>{label}</label>}
            <div className={multiline
                ? 'px-2 py-1.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors group'
                : boxClass} onClick={() => setEditing(true)}>
                {multiline ? (
                    <div className="flex items-start justify-between gap-2">
                        <span className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {(value != null && value !== '') ? (display ?? value) : <span className="text-gray-400">{placeholder}</span>}
                        </span>
                        <svg className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </div>
                ) : (
                    <>
                        <span className={valueClass}>
                            {(value != null && value !== '')
                                ? (display ?? value)
                                : <span className={accent === 'emerald' ? 'text-gray-400 font-normal text-sm' : 'text-gray-400'}>{placeholder}</span>}
                        </span>
                        <svg className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </>
                )}
            </div>
        </div>
    );
}
